import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { Checkmark20Regular, Dismiss20Regular, Folder20Filled } from '@fluentui/react-icons';
import React from 'react';
import { customPalette } from '../../theme';
import { isInvalidFolderName } from '../../utils/FoldersUtils';
import { validNameRuleRegExp } from '../../utils/RegExpUtils';
import CustomTextField from '../common/TextField';

export type FolderListRowMode = 'display' | 'edit';

export interface FolderListRowProps {
    mode: FolderListRowMode;
    id?: string;

    /** display mode */
    label?: string;
    onSelect?: () => void;
    isSelected?: boolean;

    /** edit mode — controlled */
    value?: string;
    onChange?: (value: string) => void;
    onConfirm?: () => void;
    onCancel?: () => void;
    isProcessing?: boolean;
    isError?: boolean;
    autoFocus?: boolean;
    processingLabel?: string;

    /** a11y — parent passes i18n strings */
    selectAriaLabel?: string;
    inputAriaLabel?: string;
    confirmAriaLabel?: string;
    cancelAriaLabel?: string;
}

const useStyles = makeStyles({
    editRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0px 4px',
        gap: tokens.spacingHorizontalXS,
        width: '100%',
    },
    editGrow: {
        flexGrow: 1,
        minWidth: 0,
    },
    folderButton: {
        width: '100%',
        height: '44px',
        minWidth: '0',
        justifyContent: 'flex-start',
        color: customPalette.blueDark,
        // Keep the button chrome transparent so ListItem owns hover/selection paint
        // (TOC TreeItemLayout + folder-filter subtle hover tokens).
        backgroundColor: 'transparent',
        ':hover': {
            backgroundColor: 'transparent',
            color: customPalette.blueDark,
        },
        ':hover:active': {
            backgroundColor: 'transparent',
            color: customPalette.blueDark,
        },
    },
    folderIcon: {
        color: customPalette.yellowDark,
    },
});

const FolderListRow: React.FC<FolderListRowProps> = ({
    mode,
    id,
    label,
    onSelect,
    isSelected = false,
    value = '',
    onChange,
    onConfirm,
    onCancel,
    isProcessing = false,
    isError = false,
    autoFocus = true,
    processingLabel,
    selectAriaLabel,
    inputAriaLabel,
    confirmAriaLabel,
    cancelAriaLabel,
}) => {
    const styles = useStyles();

    const handleChange = (_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, nextValue: string) => {
        if (validNameRuleRegExp.test(nextValue)) {
            onChange?.(nextValue);
        }
    };

    if (mode === 'edit') {
        return (
            <div className={styles.editRow} id={id}>
                <Folder20Filled className={styles.folderIcon} />
                <div className={styles.editGrow}>
                    <CustomTextField
                        id={id ? `${id}-input` : 'new-folder-input'}
                        onChange={handleChange}
                        value={value}
                        ariaLabel={inputAriaLabel}
                        onEnter={onConfirm}
                        isProcessing={isProcessing}
                        isErrorInput={isError}
                        processingLabel={processingLabel}
                        autoFocus={autoFocus}
                        disabled={isProcessing}
                    />
                </div>
                <Button
                    appearance='subtle'
                    icon={<Checkmark20Regular />}
                    aria-label={confirmAriaLabel}
                    onClick={onConfirm}
                    disabled={isProcessing || Boolean(isInvalidFolderName(value))}
                />
                <Button
                    appearance='subtle'
                    icon={<Dismiss20Regular />}
                    aria-label={cancelAriaLabel}
                    onClick={onCancel}
                    disabled={isProcessing}
                />
            </div>
        );
    }

    return (
        <Button
            id={id}
            appearance='subtle'
            className={styles.folderButton}
            icon={<Folder20Filled className={styles.folderIcon} />}
            aria-label={selectAriaLabel ?? label}
            aria-pressed={isSelected}
            onClick={onSelect}
        >
            {label}
        </Button>
    );
};

export default FolderListRow;
